import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { ExerciseGroup as ExerciseGroupT } from '@heraion/shared';

import type { ConfirmFn } from '../seams';
import type { PickerStyles } from './pickerStyles';
import { stateAria } from "../a11y";

export type GroupOption = {
  id: string;
  kind: 'superset' | 'circuit';
  index: number;
};

export type PairEntry = { idx: number; slug: string; name: string };

export type SupersetContext = {
  // The exercise whose ＋ opened the picker (auto-included in a new group).
  currentName: string | null;
  // Existing supersets in this workout — joining the current entry to one.
  existingGroups: ReadonlyArray<GroupOption>;
  // The workout's OTHER standalone exercises — pick to pair WITHOUT re-adding
  // (idea 2: build a superset from exercises already in the WO).
  otherEntries: ReadonlyArray<PairEntry>;
};

// Header section shown ABOVE the shared exercise list on the Superset tab
// (inbox 963ca6f2 §4). Surfaces, in one view: the current exercise, existing
// supersets to join, other WO exercises to pair, and saved templates to insert
// in one tap. The list below (multi-select) supplies brand-new members; the
// footer commits "Create superset".
// RW-25 — the template-delete confirm goes through the seam. The note that
// used to sit here said the web mount "needs a confirm adapter BEFORE exposing
// template deletion"; by then it was already exposed — PlanScreen wires
// onRemoveTemplate today — so on the web the delete was reachable and did
// nothing at all. The prediction was right, the tense was wrong.
export function SupersetPanel({
  context,
  templates,
  selectedPairIdxs,
  onTogglePair,
  onJoinGroup,
  onInsertTemplate,
  onRemoveTemplate,
  confirm,
  nameOf,
  styles,
}: {
  context: SupersetContext | null;
  templates: ReadonlyArray<ExerciseGroupT>;
  selectedPairIdxs: ReadonlySet<number>;
  onTogglePair: (idx: number) => void;
  onJoinGroup: (group: GroupOption) => void;
  onInsertTemplate: (group: ExerciseGroupT) => void;
  onRemoveTemplate: (id: string) => void;
  confirm: ConfirmFn;
  nameOf: (slug: string) => string;
  styles: PickerStyles;
}) {
  const { t } = useTranslation();

  const confirmRemoveTemplate = (g: ExerciseGroupT) => {
    void confirm({
      title: g.name,
      body: t('mobile.superset.templateDeleteBody', {
        defaultValue: 'Remove this saved superset?',
      }),
      confirmLabel: t('workout.delete', { defaultValue: 'Delete' }),
      cancelLabel: t('mobile.logger.cancel', { defaultValue: 'Cancel' }),
    }).then((ok) => {
      if (ok) onRemoveTemplate(g.id);
    });
  };

  // The anchor (the exercise whose ＋ was tapped) is NOT here: it lives in
  // the pinned SupersetContextStrip, above the search, so it stays on screen
  // while this panel scrolls away with the catalog (LG-32, 2026-08-09).
  // Everything left is a shortcut — join, pair, insert a template — and with
  // none of them there is nothing to show, so the panel takes no height.
  const hasShortcuts =
    (context?.existingGroups.length ?? 0) > 0 ||
    (context?.otherEntries.length ?? 0) > 0 ||
    templates.length > 0;
  if (!hasShortcuts) return null;

  return (
    <View style={styles.panel}>
      {context && context.existingGroups.length > 0 ? (
        <>
          <Text accessibilityRole="header" style={styles.panelLabel}>
            {t('mobile.superset.joinExisting', { defaultValue: 'Join existing' })}
          </Text>
          <View style={styles.chipRow}>
            {context.existingGroups.map((g) => (
              <Pressable
                key={g.id}
                accessibilityRole="button"
                onPress={() => onJoinGroup(g)}
                hitSlop={{ top: 4, bottom: 4 }}
                style={({ pressed }) => [styles.chip, pressed && styles.chipPressed]}
              >
                <Text style={styles.chipText}>
                  {t('mobile.group.supersetN', { n: g.index, defaultValue: `Superset ${g.index}` })}
                </Text>
              </Pressable>
            ))}
          </View>
        </>
      ) : null}

      {context && context.otherEntries.length > 0 ? (
        <>
          <Text accessibilityRole="header" style={styles.panelLabel}>
            {t('mobile.superset.pairInWorkout', { defaultValue: 'Pair with (in this workout)' })}
          </Text>
          <View style={styles.chipRow}>
            {context.otherEntries.map((e) => {
              const on = selectedPairIdxs.has(e.idx);
              return (
                <Pressable
                  key={e.idx}
                  accessibilityRole="button"
                  accessibilityState={{ selected: on }}
                  {...stateAria({ selected: on }, "button")}
                  onPress={() => onTogglePair(e.idx)}
                  hitSlop={{ top: 4, bottom: 4 }}
                  style={({ pressed }) => [
                    styles.chip,
                    on && styles.chipActive,
                    pressed && styles.chipPressed,
                  ]}
                >
                  <Text style={[styles.chipText, on && styles.chipTextActive]} numberOfLines={1}>
                    {e.name}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </>
      ) : null}

      {templates.length > 0 ? (
        <>
          <Text accessibilityRole="header" style={styles.panelLabel}>
            {t('mobile.superset.templates', { defaultValue: 'Saved supersets' })}
          </Text>
          <View style={styles.chipRow}>
            {templates.map((g) => (
              <Pressable
                key={g.id}
                accessibilityRole="button"
                accessibilityHint={t('mobile.superset.templateInsertHint', {
                  defaultValue: 'Insert this superset; long-press to delete',
                })}
                onPress={() => onInsertTemplate(g)}
                onLongPress={() => confirmRemoveTemplate(g)}
                hitSlop={{ top: 4, bottom: 4 }}
                style={({ pressed }) => [styles.chip, pressed && styles.chipPressed]}
              >
                <Text style={styles.chipText}>
                  {g.name}{' '}
                  <Text style={styles.templateMeta}>
                    {g.exerciseSlugs.map(nameOf).join(' + ')}
                  </Text>
                </Text>
              </Pressable>
            ))}
          </View>
        </>
      ) : null}

      <View style={styles.panelDivider} />
    </View>
  );
}
